/**
 * 本地配送（type 'local'）的配送费档位 —— 与后端 `AddOrderForm::getBaseFee`（contactfree-backend
 * `api/models/form/AddOrderForm.php` 3160-3205）的 local 分支同义：
 *
 *   baseFee = 设置里的 baseFee；minDeliveryAmount = 设置里的起送额
 *   遍历**全部** conditionRule：客户端报的 subtotal >= rule.minDeliveryAmount 就覆盖一次，**最后一条满足的赢**
 *   （不是门槛最高的那条 —— 规则按什么顺序存就按什么顺序比）；
 *   覆盖值 = rule.baseFee 与设置 baseFee 取小（`rule.baseFee > baseFee ? baseFee : rule.baseFee`）。
 *
 * 以前购物车只看 `conditionRule[0]`（审计 UL-9）：有两条以上规则时报给后端的配送费 / 起送额对不上，
 * 游客单差额 `##9##`、登录用户 `##8##`。期望值见 tests/unit/fixtures/surcharge/php-getBaseFee-cases.json（后端方法实跑）。
 */
import { phpFloat } from '@/utils/phpSemantics'

// PHP 7.4 `$subtotal >= $rule['minDeliveryAmount']`：null / 缺失按真值比（永远成立），其余按数比
function reachesMin(subtotal, min) {
  if (min === null || min === undefined) return true
  return phpFloat(subtotal) >= phpFloat(min)
}

/**
 * @param {object} deliverySetting get-venue 的 settings.deliverySettings
 * @param {number} subtotal 送给后端的 subtotal（购物车小计 getters.total）
 * @returns {{ matched: boolean, baseFee: number, minDeliveryAmount: * }}
 *   matched = 有规则生效；baseFee 为数；minDeliveryAmount 原样（报给后端的 details.minDeliveryAmount）
 */
export function resolveLocalDeliveryFee(deliverySetting, subtotal) {
  const setting = deliverySetting || {}
  let baseFee = setting.baseFee
  let minDeliveryAmount = setting.minDeliveryAmount
  let matched = false
  const rules = Array.isArray(setting.conditionRule) ? setting.conditionRule : []
  rules.forEach(rule => {
    if (!rule || typeof rule !== 'object') return
    if (reachesMin(subtotal, rule.minDeliveryAmount)) {
      matched = true
      minDeliveryAmount = rule.minDeliveryAmount
      baseFee = phpFloat(rule.baseFee) > phpFloat(setting.baseFee) ? setting.baseFee : rule.baseFee
    }
  })
  return { matched, baseFee: phpFloat(baseFee), minDeliveryAmount }
}

/**
 * 后端 getBaseFee 的分支判断：`$deliverySettingArr['type'] != 'local'` → 第三方。缺 `type` 也是第三方
 * （review 第 1 轮 #6：购物车以前一处把缺 type 当本地看档位、一处当第三方取报价）。
 * @param {object} deliverySetting get-venue 的 settings.deliverySettings
 * @returns {boolean}
 */
export function isLocalDelivery(deliverySetting) {
  return !!deliverySetting && deliverySetting.type === 'local'
}

/**
 * 购物车定配送费：与后端 getBaseFee 同义。
 *   第三方（type 不是 'local'，含缺 type）：费用 = 第三方报价 `deliveryCost.userCost`；没有报价时后端读不到缓存
 *     （`$userDelivery['cost'] / 100`，null / 100 = 0）按 0 收；不看档位；起送额用门店设置。
 *   本地：看全部档位（resolveLocalDeliveryFee），满足时覆盖配送费与起送额，并记为 conditionBaseFee。
 * 配送费**不取整**（后端 `$order->amount += $baseFee` 原样加，第三方报价可以有不足一分的零头）。
 * @param {object} deliverySetting get-venue 的 settings.deliverySettings
 * @param {object} deliveryInfo contactDetails.deliveryInfo[门店]（取第三方报价）
 * @param {number} subtotal 报给后端的 subtotal（getters.total）
 * @returns {{ baseFee: number, minDeliveryAmount: *, conditionBaseFee: number|'' }}
 *   baseFee = 报给后端的 details.baseFee；conditionBaseFee = 写回 deliveryInfo 的档位配送费（没命中为 ''）
 */
export function deliveryQuote(deliverySetting, deliveryInfo, subtotal) {
  const setting = deliverySetting || {}
  if (!isLocalDelivery(setting)) {
    const userCost = deliveryInfo && deliveryInfo.deliveryCost ? deliveryInfo.deliveryCost.userCost : undefined
    return { baseFee: phpFloat(userCost ?? 0), minDeliveryAmount: setting.minDeliveryAmount, conditionBaseFee: '' }
  }
  const condition = resolveLocalDeliveryFee(setting, subtotal)
  if (condition.matched) return { baseFee: condition.baseFee, minDeliveryAmount: condition.minDeliveryAmount, conditionBaseFee: condition.baseFee }
  return { baseFee: phpFloat(setting.baseFee ?? 0), minDeliveryAmount: setting.minDeliveryAmount, conditionBaseFee: '' }
}

/**
 * 已经定好的配送费（购物车按 deliveryQuote 写回了 conditionBaseFee 之后）：总额里加的（getters._deliveryBaseFee）
 * 与付款页 / 卡页上报的 details.baseFee 都从这里取，和购物车报的是同一个数。
 * @param {object} deliverySetting
 * @param {object} deliveryInfo
 * @returns {number}
 */
export function deliveryFeeFor(deliverySetting, deliveryInfo) {
  const setting = deliverySetting || {}
  const info = deliveryInfo || {}
  if (!isLocalDelivery(setting)) return phpFloat((info.deliveryCost ? info.deliveryCost.userCost : undefined) ?? 0)
  if (typeof info.conditionBaseFee === 'number') return info.conditionBaseFee
  return phpFloat(setting.baseFee ?? 0)
}
